import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import WarehouseLocationForm from '@/components/warehouse/WarehouseLocationForm';
import WarehouseService from '@/api/warehouses.service';

interface WarehouseLocationFormData {
  code: string;
  name: string;
  description: string;
  type: string;
  capacity: number | '';
  dimensions: string;
  status: 'active' | 'inactive';
}

const CreateWarehouseLocation: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [warehouseName, setWarehouseName] = useState<string>('');

  const warehouseId = searchParams.get('warehouseId');

  useEffect(() => {
    if (warehouseId) {
      fetchWarehouseName();
    }
  }, [warehouseId]);

  const fetchWarehouseName = async () => {
    if (!warehouseId) return;
    
    try {
      const response = await WarehouseService.getWarehouse(warehouseId);
      if (response.data?.success) {
        setWarehouseName(response.data.warehouse.name);
      }
    } catch (error) {
      console.error('Error fetching warehouse:', error);
    }
  };

  const handleSubmit = async (formData: WarehouseLocationFormData) => {
    if (!warehouseId) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Warehouse ID is required",
      });
      return;
    }

    setIsLoading(true);
    
    try {
      // Prepare data for API
      const locationData = {
        code: formData.code,
        name: formData.name,
        description: formData.description || undefined,
        type: formData.type,
        capacity: formData.capacity === '' ? undefined : Number(formData.capacity),
        dimensions: formData.dimensions || undefined,
        status: formData.status
      };

      const response = await WarehouseService.createWarehouseLocation(warehouseId, locationData);

      if (response.data?.success) {
        toast({
          title: "Success",
          description: "Warehouse location created successfully",
        });
        navigate('/warehouse/locations');
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: response.data?.message || "Failed to create warehouse location",
        });
      }
    } catch (error: any) {
      console.error('Error creating warehouse location:', error);
      
      let errorMessage = "Failed to create warehouse location";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast({
        variant: "destructive",
        title: "Error",
        description: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/warehouse/locations');
  };

  if (!warehouseId) {
    return (
      <div className="text-center py-8">
        <h3 className="text-lg font-medium text-gray-900">Warehouse ID Required</h3>
        <p className="text-gray-500">Please select a warehouse to create a location.</p>
        <Button onClick={() => navigate('/warehouse/locations')} className="mt-4">
          Back to Locations
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/warehouse/locations')}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Locations
        </Button>
      </div>

      <div>
        <h1 className="text-3xl font-bold tracking-tight">Create New Location</h1>
        <p className="text-muted-foreground">
          Add a new location to {warehouseName || 'the selected warehouse'}
        </p>
      </div>

      {/* Form */}
      <WarehouseLocationForm
        mode="create"
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isLoading={isLoading}
      />
    </div>
  );
};

export default CreateWarehouseLocation; 